import { useState, useEffect } from 'react';
import { X, Crown, Check, AlertCircle, Calendar, Zap, TrendingDown, Save, Download, Shield, XCircle, RefreshCw } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { SubscriptionModal } from './SubscriptionModal';
import { SubscriptionTier, getSubscriptionPricing, PricingInfo } from '../services/api';
import { exportAndDownloadData } from '../services/export';

interface SubscriptionManagementProps {
  isOpen: boolean;
  onClose: () => void;
}

const TIER_INFO: Record<SubscriptionTier, { name: string; color: string; icon: typeof Crown }> = {
  free: { name: 'Free', color: 'text-neutral-600 dark:text-neutral-400', icon: Zap },
  pro: { name: 'Pro', color: 'text-blue-600 dark:text-blue-400', icon: Crown },
  business: { name: 'Business', color: 'text-purple-600 dark:text-purple-400', icon: Crown },
  enterprise: { name: 'Enterprise', color: 'text-accent-600 dark:text-accent-400', icon: Crown },
};

export function SubscriptionManagement({ isOpen, onClose }: SubscriptionManagementProps) {
  const { subscription, loading, error, refresh, upgrade } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [pricing, setPricing] = useState<Record<string, PricingInfo> | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPricing();
    }
  }, [isOpen]);

  const loadPricing = async () => {
    try {
      const response = await getSubscriptionPricing();
      setPricing(response.pricing);
    } catch (error) {
      console.error('Error loading pricing:', error);
    }
  };

  if (!isOpen) return null;

  const currentTierInfo = subscription ? TIER_INFO[subscription.tier] : TIER_INFO.free;
  const CurrentTierIcon = currentTierInfo.icon;
  const isPremium = subscription?.is_premium ?? false;
  const expiresAt = subscription?.expires_at ? new Date(subscription.expires_at) : null;
  const isExpired = expiresAt ? expiresAt < new Date() : false;

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleUpgrade = async (tier: SubscriptionTier) => {
    try {
      await upgrade(tier);
      setShowUpgradeModal(false);
      // Refresh subscription status after upgrade
      setTimeout(() => {
        refresh();
      }, 2000);
    } catch (err) {
      console.error('Upgrade error:', err);
    }
  };

  const handleExportData = async () => {
    if (!subscription?.can_export) {
      alert('Data export is only available for Business and Enterprise plans. Please upgrade to access this feature.');
      setShowUpgradeModal(true);
      return;
    }

    setExporting(true);
    try {
      await exportAndDownloadData();
      // Show success message (could be a toast notification)
      alert('Data exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 z-10">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Subscription Management</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">View and manage your subscription</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
            >
              <X size={20} className="text-neutral-600 dark:text-neutral-400" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">Error loading subscription</p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>
                </div>
                <button
                  onClick={refresh}
                  className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                >
                  <RefreshCw size={16} className="text-red-600 dark:text-red-400" />
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && !subscription && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600 mx-auto mb-4"></div>
                <p className="text-neutral-500 dark:text-neutral-400">Loading subscription details...</p>
              </div>
            </div>
          )}

          {/* Current Subscription Status */}
          {subscription && (
            <>
              <div className="bg-gradient-to-r from-accent-50 to-accent-100 dark:from-accent-900/20 dark:to-accent-800/20 rounded-xl p-6 border border-accent-200 dark:border-accent-800">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg bg-white dark:bg-neutral-800 ${currentTierInfo.color}`}>
                      <CurrentTierIcon size={24} className={currentTierInfo.color} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                          {currentTierInfo.name} Plan
                        </h3>
                        {isPremium && (
                          <span className="px-2 py-1 text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                            Active
                          </span>
                        )}
                        {isExpired && (
                          <span className="px-2 py-1 text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full">
                            Expired
                          </span>
                        )}
                      </div>
                      {expiresAt && (
                        <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                          <Calendar size={16} />
                          <span>
                            {isExpired ? 'Expired on' : 'Renews on'} {formatDate(expiresAt)}
                          </span>
                        </div>
                      )}
                      {!expiresAt && !isPremium && (
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Free tier - no expiration</p>
                      )}
                    </div>
                  </div>
                  {isPremium && pricing && (
                    <div className="text-right">
                      <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                        {pricing[subscription.tier.toUpperCase()]?.formatted_price || 'N/A'}
                      </div>
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">
                        per {pricing[subscription.tier.toUpperCase()]?.period || 'month'}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Subscription Limits */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
                  <div className="flex items-center gap-2 mb-2">
                    <Save size={18} className="text-blue-600 dark:text-blue-400" />
                    <h4 className="font-semibold text-neutral-900 dark:text-neutral-100">Saved Searches</h4>
                  </div>
                  <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                    {subscription.max_saved_searches === -1 ? '∞' : subscription.max_saved_searches}
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    {subscription.max_saved_searches === -1 ? 'Unlimited' : 'Maximum allowed'}
                  </p>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown size={18} className="text-orange-600 dark:text-orange-400" />
                    <h4 className="font-semibold text-neutral-900 dark:text-neutral-100">Price Alerts</h4>
                  </div>
                  <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                    {subscription.max_price_alerts === -1 ? '∞' : subscription.max_price_alerts}
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    {subscription.max_price_alerts === -1 ? 'Unlimited' : 'Maximum allowed'}
                  </p>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Download size={18} className="text-green-600 dark:text-green-400" />
                      <h4 className="font-semibold text-neutral-900 dark:text-neutral-100">Data Export</h4>
                    </div>
                    {subscription.can_export && (
                      <button
                        onClick={handleExportData}
                        disabled={exporting}
                        className="px-3 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                      >
                        <Download size={14} />
                        {exporting ? 'Exporting...' : 'Export'}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {subscription.can_export ? (
                      <>
                        <Check size={20} className="text-green-500 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-700 dark:text-green-400">Enabled</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={20} className="text-neutral-400" />
                        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Not Available</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield size={18} className="text-purple-600 dark:text-purple-400" />
                    <h4 className="font-semibold text-neutral-900 dark:text-neutral-100">Ad-Free</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    {subscription.ad_free ? (
                      <>
                        <Check size={20} className="text-green-500 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-700 dark:text-green-400">Enabled</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={20} className="text-neutral-400" />
                        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Ads Shown</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                {!isPremium && (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Crown size={18} />
                    Upgrade Plan
                  </button>
                )}
                {isPremium && subscription.tier !== 'enterprise' && (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Zap size={18} />
                    Upgrade Plan
                  </button>
                )}
                <button
                  onClick={refresh}
                  className="px-6 py-3 bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 text-neutral-900 dark:text-neutral-100 font-medium rounded-lg transition-colors flex items-center gap-2"
                >
                  <RefreshCw size={18} />
                  Refresh Status
                </button>
                {isPremium && (
                  <button
                    onClick={() => {
                      // TODO: Implement cancel subscription
                      alert('Cancel subscription functionality coming soon. Please contact support.');
                    }}
                    className="px-6 py-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-700 dark:text-red-400 font-medium rounded-lg transition-colors flex items-center gap-2"
                  >
                    <XCircle size={18} />
                    Cancel Subscription
                  </button>
                )}
              </div>

              {/* Upgrade Prompt for Free Users */}
              {!isPremium && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <Zap size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">Unlock Premium Features</h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                        Upgrade to Pro or higher to get unlimited searches, more price alerts, data export, and an ad-free experience.
                      </p>
                      <button
                        onClick={() => setShowUpgradeModal(true)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors"
                      >
                        View Plans
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Upgrade Modal */}
      <SubscriptionModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={subscription?.tier ?? 'free'}
        onUpgrade={handleUpgrade}
        loading={loading}
      />
    </div>
  );
}

